import React from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPaymentRateSettings,
  updatePaymentRateSettings,
} from '../api/settings';
import { useAuth } from '../auth/AuthContext';

export const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canEdit = role === 'admin' || role === 'drh';
  const { data, isLoading } = useQuery({
    queryKey: ['payment-rate-settings'],
    queryFn: getPaymentRateSettings,
  });

  const [hoursRate, setHoursRate] = React.useState('');
  const [daysRate, setDaysRate] = React.useState('');
  const [monthsRate, setMonthsRate] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!data) return;
    setHoursRate(String(data.hours ?? ''));
    setDaysRate(String(data.days ?? ''));
    setMonthsRate(String(data.months ?? ''));
  }, [data]);

  const handleSave = async () => {
    const hours = Number(hoursRate.replace(',', '.'));
    const days = Number(daysRate.replace(',', '.'));
    const months = Number(monthsRate.replace(',', '.'));
    if (![hours, days, months].every((n) => Number.isFinite(n) && n >= 0)) {
      setError('All rates must be valid positive numbers.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updatePaymentRateSettings({ hours, days, months });
      await queryClient.invalidateQueries({ queryKey: ['payment-rate-settings'] });
      setSuccess('Payment rates updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2, maxWidth: 720 }}>
      <Typography variant="h5" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure payment amount per activity duration unit.
      </Typography>
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view rates, but only administrators and DRH can edit them.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Rate per hour (CFA)"
          value={hoursRate}
          onChange={(e) => setHoursRate(e.target.value)}
          disabled={isLoading || saving || !canEdit}
        />
        <TextField
          label="Rate per day (CFA)"
          value={daysRate}
          onChange={(e) => setDaysRate(e.target.value)}
          disabled={isLoading || saving || !canEdit}
        />
        <TextField
          label="Rate per month (CFA)"
          value={monthsRate}
          onChange={(e) => setMonthsRate(e.target.value)}
          disabled={isLoading || saving || !canEdit}
        />
        <Box>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={isLoading || saving || !canEdit}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

